<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import type { Emitter } from 'mitt';

import { useToast } from 'primevue/usetoast';
import { isNavigationFailure, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/modules/auth/store/auth.store';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/core/stores/useAppStore';

const { traceId } = storeToRefs(useAppStore());
const { setTraceId, setVersion } = useAppStore();

const bus = inject<Emitter<TBusEvent>>('bus') as Emitter<TBusEvent>;
const toast = useToast();
const loading = ref(false);
const newVersionAvailable = ref(false);

const router = useRouter();
const route = useRoute();

let redirectLoginInProgress = false;
const redirectLogin = async () => {
  if (redirectLoginInProgress) {
    return;
  }
  redirectLoginInProgress = true;

  return await router.push({
    name: 'login',
    query: {
      redirect_uri: window.location.pathname,
    },
  });
};

let routerReady = false;
let redirectOnMount = false;

onMounted(async () => {
  try {
    await router.isReady();
  } catch (e) {
    const navGuardReject = isNavigationFailure(e);
    if (!navGuardReject) {
      throw e;
    }
  }

  routerReady = true;
  if (redirectOnMount) {
    redirectLogin();
  }
});

const authStore = useAuthStore();
const { initialized } = storeToRefs(authStore);

const switchBeta = () => {
  // const user = useUser();
  // router.removeRoute('home');
  // router.addRoute({
  //   name: 'home',
  //   path: '/home',
  //   redirect: { name: user.ephemeral.beta ? 'allrecruitment' : 'allstudies' },
  // });
};
bus.on('buildNumberChanged', (build: string) => {
  newVersionAvailable.value = true;
});
bus.on('buildNumberInitial', (build: string) => {
  setVersion(build);
});
bus.on('redirectLogin', () => {
  if (routerReady) {
    redirectLogin();
  } else {
    redirectOnMount = true;
  }
});
bus.on('user.beta', () => {
  switchBeta();
});

bus.on('toast', (event) => {
  toast.add(event);
});

bus.on('loading_app', (isLoading) => {
  // TODO: Figure out if we want such a generic top level loader
  // loading.value = isLoading
});

bus.on('trace_id', (traceId) => {
  setTraceId(traceId);
});

const layout = computed(() => {
  return 'layout-' + (route.meta.layout ?? 'empty');
});

const copyTraceId = async (e: any) => {
  const val = e.target.innerHTML;
  navigator.clipboard
    .writeText(val)
    .then(() => {
      toast.add({
        severity: 'success',
        summary: `Trace ID copied: ${val}`,
        life: 4000,
      });
    })
    .then(() => {
      traceId.value = null;
    });
};

const reloadPage = () => {
  window.location.reload();
};


const dismissNewVersion = () => {
  newVersionAvailable.value = false;
};
const currentUrl = window.location.href;
</script>

<template>
  <component
    :is="layout"
    v-if="(initialized && route.meta.requiresAuth) || !route.meta.requiresAuth"
  />
  <Toast />
  <ConfirmPopup :pt="{ root: { class: 'max-w-96' } }" group="inline" />
  <ConfirmDialog />
  <DynamicDialog />
  <Dialog
    v-model:visible="loading"
    modal
    :show-header="false"
    :closable="false"
    :style="{ width: '200px', height: '200px' }"
    :pt="{ content: { class: 'h-full pt-4' } }"
  >
    <div class="h-full w-full flex items-center justify-center">
      <ProgressSpinner
        style="width: 100px; height: 100px"
        strokeWidth="8"
        aria-label="Loading..."
      />
    </div>
  </Dialog>
  <Message
    v-if="traceId"
    class="font-mono text-slate-600 m-0 fixed bottom-0 left-0 z-50"
    severity="warn"
    size="small"
    >Error trace:
    <span @click.stop="copyTraceId" class="cursor-copy">{{
      traceId
    }}</span></Message
  >
  <Transition name="slide-fade">
    <div
      class="fixed top-0 left-0 right-0 flex flex-row justify-center z-50 pointer-events-none"
      v-if="newVersionAvailable"
    >
      <div
        class="bg-slate-50 rounded-b-xl border border-slate-300 text-slate-500 p-2 px-4 border-t-0 flex flex-row gap-4 items-center pointer-events-auto"
      >
        <span
          ><span class="font-semibold">New version available! </span>&nbsp;
          <a
            class="underline text-primary-400"
            :href="currentUrl"
            @click.prevent="reloadPage"
            >Reload</a
          >
          the page now to get the latest version.</span
        ><Button
          size="small"
          severity="secondary"
          label="Dismiss"
          @click="dismissNewVersion"
        ></Button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
header {
  line-height: 1.5;
  max-height: 100vh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}
</style>

<style>
/*
  Enter and leave animations can use different
  durations and timing functions.
*/
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-100%);
}
</style>
